import React, { useMemo, useState } from "react";
import authFetch from "../authFetch";

/* ══ MISSING TRANSLATIONS AUDIT ═════════════════════════════
   Scans every workspace-level entity that has the {name, nameAr}
   shape and counts how many are missing their Arabic counterpart.
   Surfaces a one-click "Translate all with AI" action that walks the
   list, batching items into Claude Haiku calls and dispatching the
   right reducer action per source.

   Currently audits:
     - expense categories  → RENAME_CAT
     - income categories   → RENAME_INCOME_CAT
     - investment categories → UPDATE_INV_CAT (key: name)

   Could be extended to locations, suppliers, projects, vehicles, etc.
   by adding entries to AUDIT_SOURCES. */

const AUDIT_SOURCES = [
  { key:"expenseCats", labelEn:"Expense categories", labelAr:"فئات المصروفات",
    pick: (state) => state.expenseCats || [],
    dispatchUpdate: (dispatch, item, nameAr) => dispatch({ type:"RENAME_CAT", id:item.id, nameAr }),
    domain: "category" },
  { key:"incomeCats", labelEn:"Income categories", labelAr:"فئات الدخل",
    pick: (state) => state.incomeCats || [],
    dispatchUpdate: (dispatch, item, nameAr) => dispatch({ type:"RENAME_INCOME_CAT", id:item.id, nameAr }),
    domain: "category" },
  { key:"investmentCategories", labelEn:"Investment categories", labelAr:"فئات الاستثمار",
    pick: (state) => state.investmentCategories || [],
    dispatchUpdate: (dispatch, item, nameAr) => dispatch({ type:"UPDATE_INV_CAT", name:item.name, patch:{ nameAr } }),
    domain: "investment category" },
];

async function batchTranslate(items, domain) {
  if (!items.length) return {};
  const r = await authFetch("/api/ai-chat", {
    method:"POST",
    headers:{"Content-Type":"application/json"},
    body: JSON.stringify({
      model: "claude-haiku-4-5-20251001",
      max_tokens: Math.min(2000, 60 + items.length * 30),
      messages: [{
        role:"user",
        content: `Translate these ${domain} names from English to Arabic. Return ONLY a JSON object where each key is the original English string and value is the Arabic translation. No markdown, no extra text.\n\nItems: ${JSON.stringify(items)}`
      }]
    })
  });
  const d = await r.json();
  const raw = (d.content?.[0]?.text || "{}").replace(/```json|```/g, "").trim();
  try { return JSON.parse(raw); } catch { return {}; }
}

export default function MissingTranslationsCard({ state, dispatch, lang }) {
  const isRTL = lang === "ar";
  const [running, setRunning] = useState(null);
  const [progress, setProgress] = useState(null);

  const audit = useMemo(() => AUDIT_SOURCES.map(src => {
    const items = src.pick(state);
    const missing = items.filter(it => it && it.name && !(it.nameAr && it.nameAr.trim()));
    return { ...src, items, missing };
  }), [state]);
  const totalMissing = audit.reduce((s, a) => s + a.missing.length, 0);
  const totalItems = audit.reduce((s, a) => s + a.items.length, 0);
  const coverage = totalItems ? Math.round(((totalItems - totalMissing) / totalItems) * 100) : 100;

  const translateOne = async (src) => {
    if (running) return;
    setRunning(src.key);
    setProgress({ key: src.key, done: 0, total: src.missing.length });
    /* Batch in groups of 25 to keep each API call snappy. */
    const groups = [];
    for (let i = 0; i < src.missing.length; i += 25) groups.push(src.missing.slice(i, i + 25));
    let done = 0;
    for (const group of groups) {
      const names = group.map(it => it.name);
      const map = await batchTranslate(names, src.domain);
      group.forEach(item => {
        const ar = (map[item.name] || "").trim();
        if (ar) src.dispatchUpdate(dispatch, item, ar);
        done += 1;
        setProgress({ key: src.key, done, total: src.missing.length });
      });
    }
    setRunning(null);
    setProgress(null);
  };

  const translateAll = async () => {
    for (const src of audit) {
      if (src.missing.length) await translateOne(src);
    }
  };

  return (
    <div className="card" style={{marginBottom:18, border:`1.5px solid ${totalMissing ? "var(--gold-bd)" : "var(--grn-bd)"}`}}>
      <div style={{padding:"14px 18px", borderBottom:"1px solid var(--g100)", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap", background: totalMissing ? "var(--gold-bg)" : "var(--grn-bg)"}}>
        <div>
          <div style={{fontSize:13, fontWeight:700, color: totalMissing ? "var(--gold)" : "var(--grn)", textTransform:"uppercase", letterSpacing:".06em", marginBottom:3}}>
            {isRTL ? "تدقيق الترجمات" : "Translation audit"}
          </div>
          <div style={{fontSize:12, color:"var(--g600)"}}>
            {totalMissing === 0
              ? (isRTL ? "كل الفئات والأنواع لها أسماء عربية ✓" : "Every category has an Arabic name ✓")
              : (isRTL
                  ? `${totalMissing} من ${totalItems} عنصر بحاجة إلى اسم عربي · التغطية ${coverage}%`
                  : `${totalMissing} of ${totalItems} entries missing Arabic · ${coverage}% coverage`)
            }
          </div>
        </div>
        {totalMissing > 0 && (
          <button
            className="btn btn-p btn-sm"
            disabled={running !== null}
            onClick={translateAll}
            style={{flexShrink:0}}
          >
            {running
              ? (isRTL ? `جارٍ الترجمة… ${progress?.done||0}/${progress?.total||0}` : `Translating… ${progress?.done||0}/${progress?.total||0}`)
              : (isRTL ? `ترجمة الكل بالذكاء الاصطناعي (${totalMissing})` : `Translate all with AI (${totalMissing})`)}
          </button>
        )}
      </div>
      <div style={{padding:"4px 0"}}>
        {audit.map(src => (
          <div key={src.key} style={{display:"flex", alignItems:"center", gap:10, padding:"8px 18px", borderBottom:"1px solid var(--g100)"}}>
            <div style={{flex:1, fontSize:13, color:"var(--ink)"}}>
              {isRTL ? src.labelAr : src.labelEn}
            </div>
            <div style={{fontSize:11, color: src.missing.length ? "var(--gold)" : "var(--grn)", fontFamily:"var(--fm)", minWidth:60, textAlign: isRTL ? "left" : "right"}}>
              {src.items.length === 0
                ? (isRTL ? "—" : "—")
                : src.missing.length === 0
                  ? (isRTL ? "مكتمل ✓" : "Complete ✓")
                  : (isRTL ? `${src.missing.length} ناقص` : `${src.missing.length} missing`)}
            </div>
            {src.missing.length > 0 && (
              <button
                className="btn btn-s btn-xs"
                disabled={running !== null}
                onClick={() => translateOne(src)}
                style={{flexShrink:0}}
              >
                {running === src.key
                  ? `${progress?.done||0}/${progress?.total||0}`
                  : (isRTL ? "ترجمة" : "Translate")}
              </button>
            )}
          </div>
        ))}
      </div>
      <div style={{padding:"10px 18px", fontSize:11, color:"var(--g400)", lineHeight:1.5, borderTop:"1px solid var(--g100)"}}>
        {isRTL
          ? "يستخدم التدقيق Claude لاقتراح الترجمات لجميع الفئات. راجع النتائج من تبويب «الفئات» وعدّل أي اسم تريده."
          : "AI-translates every missing nameAr via Claude Haiku. Review results in the Categories tab and edit any you'd like to refine."}
      </div>
    </div>
  );
}
